import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { InvestmentsReadonlyTableComponent } from '@app/components/investments-readonly-table/investments-readonly-table.component';
import type { Investment } from '@app/models/investment.models';
import type { ProjectPublicResponse } from '@app/models/project.models';
import { ProjectService } from '@app/services/projects/service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-project-offers-details',
  standalone: true,
  imports: [CommonModule, TranslateModule, InvestmentsReadonlyTableComponent],
  templateUrl: './project-offers-details.component.html',
  styleUrl: './project-offers-details.component.scss',
})
export class ProjectOffersDetailsComponent implements OnInit {
  private static readonly OFFERS_TAKE = 10000;

  protected readonly loading = signal(false);
  protected readonly offers = signal<Investment[]>([]);
  protected readonly projectName = signal<string>('');
  protected readonly projectId = signal<string>('');

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectService);

  public ngOnInit(): void {
    const projectId = this.route.snapshot.paramMap.get('id') ?? '';
    this.projectId.set(projectId);
    if (projectId) {
      this.loadProjectName(projectId);
      this.loadOffers(projectId);
    }
  }

  protected goToIncomingList(): void {
    void this.router.navigate(['/profile', 'legal', 'myProjects.fundraising']);
  }

  private loadOffers(projectId: string): void {
    this.loading.set(true);
    this.projectService
      .getInvestments({
        skip: 0,
        take: ProjectOffersDetailsComponent.OFFERS_TAKE,
      })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (response) => {
          const filteredOffers = (response?.data ?? []).filter(
            (investment) => investment.projectId === projectId,
          );
          this.offers.set(filteredOffers);
          this.loading.set(false);
        },
        error: () => {
          this.offers.set([]);
          this.loading.set(false);
        },
      });
  }

  private loadProjectName(projectId: string): void {
    this.projectService
      .getProjectById(projectId)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (project: ProjectPublicResponse) => {
          this.projectName.set(project.name ?? projectId);
        },
        error: () => {
          this.projectName.set(projectId);
        },
      });
  }
}
